import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import { Skeleton } from "primereact/skeleton";
import UserService from "../../../services/UserService";
import Moment from "moment";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";


const STATUS_OPTIONS = [
  { label: "All Status", value: 0 },
  { label: "Accepted", value: 1 },
  { label: "Not Accepted", value: 2 },
];

const FirstLoginHistoryReport = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [results, setResults] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [fromDate, setFromDate] = useState(() => Moment().subtract(1, "month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(() => Moment().format("YYYY-MM-DD"));
  const [acceptanceStatus, setAcceptanceStatus] = useState(0);

  const headers = [
    { label: "S.No", key: "serialNo" },
    { label: "User Id", key: "userId" },
    { label: "User Name", key: "userName" },
    { label: "Display Name", key: "displayName" },
    { label: "Email", key: "email" },
    { label: "First Login Time", key: "firstLoginDateTime" },
    { label: "Privacy Policy Accepted Time", key: "privacyPolicyAcceptedDateTime" },
    { label: "Status", key: "status" },
  ];

  // Permissions
  const supportAccess = authContext.getPermissions("report");
  useEffect(() => {
    if (
      supportAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
  }, [supportAccess, navigate, authContext]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
      setCurrentPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const loadResults = useCallback(
    async (signal) => {
      setLoading(true);
      try {
        const skip = currentPage * rowsPerPage;
        const take = rowsPerPage;
        const from = fromDate || "";
        const to = toDate || "";

        const data = await UserService.GetFirstLoginPolicy(
          skip,
          take,
          from,
          to,
          acceptanceStatus,
          debouncedSearchText,
          authContext,
          signal
        );

        if (signal?.aborted) return;

        if (data.isSuccess) {
          const items = data.data?.records ?? [];
          const total = data.data?.totalRecords ?? items.length;
          setResults(items);
          setTotalRecords(total);
        } else {
          AlertService.error(data.errorMessage);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [currentPage, rowsPerPage, fromDate, toDate, acceptanceStatus, debouncedSearchText, authContext]
  );

useEffect(() => {
  if (!fromDate || !toDate) {
    return;
  }
  const controller = new AbortController();
  loadResults(controller.signal);

  return () => controller.abort();
}, [loadResults, fromDate, toDate]);

  const handleClearFilters = () => {
    setSearchText("");
    setFromDate(Moment().subtract(1, "month").format("YYYY-MM-DD"));
    setToDate(Moment().format("YYYY-MM-DD"));
    setAcceptanceStatus(0);
    setCurrentPage(0);
  };

  const csvData = useMemo(() => {
    return results.map((row, i) => ({
      serialNo: currentPage * rowsPerPage + i + 1,
      ...row,
      firstLoginDateTime: row.firstLoginDateTime
        ? Moment(row.firstLoginDateTime + "Z").format("DD/MM/YYYY hh:mm A")
        : "",
      privacyPolicyAcceptedDateTime: row.privacyPolicyAcceptedDateTime
        ? Moment(row.privacyPolicyAcceptedDateTime + "Z").format("DD/MM/YYYY hh:mm A")
        : "",
    }));
  }, [results, currentPage, rowsPerPage]);

  const generatePageNumbers = () => {
    const totalPages = Math.ceil(totalRecords / rowsPerPage) || 1;
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");
      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);
      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages)
        start = totalPages - 3;
      for (let i = start; i <= end; i++) {
        if (i > 1 && i < totalPages) pages.push(i);
      }
      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  // Skeleton
  const skeletonData = Array.from({ length: rowsPerPage }, (_, i) => ({
    id: `skeleton-${i}`,
  }));
  const skeletonBody = () => <Skeleton width="90%" height="1.5rem" />;

  const renderValue = (value) => {
    return value !== null && value !== undefined && value !== "" ? value : "-";
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage) || 1;

  return (
    <div className="container-fluid custom-container position-relative mb-5">
      {/* Toolbar */}
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3 pt-3">
        <div className="d-flex flex-column global-search-wrapper">
          <label className="filter-label">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setCurrentPage(0);
            }}
            disabled={loading}
            className="form-control date-filter-input custom-input-color"
            max={toDate || undefined}
          />
        </div>

        <div className="d-flex flex-column global-search-wrapper">
          <label className="filter-label">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setCurrentPage(0);
            }}
            disabled={loading}
            className="form-control date-filter-input custom-input-color"
            min={fromDate || undefined}
          />
        </div>

        <div className="d-flex flex-column global-search-wrapper">
          <label className="filter-label">Status</label>
          <Dropdown
            value={acceptanceStatus}
            onChange={(e) => {
              setAcceptanceStatus(e.value);
              setCurrentPage(0);
            }}
            options={STATUS_OPTIONS}
            optionLabel="label"
            optionValue="value"
            placeholder="Status"
            disabled={loading}
            className="custom-input-color report-dropdown"
            panelClassName="report-dropdown-panel"
            style={{ minWidth: "160px" }}
          />
        </div>

        <button
          className="btn btn-outline-secondary date-filter-input"
          onClick={handleClearFilters}
          disabled={loading}
        >
  <i className="pi pi-refresh me-1"></i>Reset
        </button>

        <div className="d-flex align-items-center flex-wrap gap-3 ms-auto">
          <div className="global-search-wrapper">
            <i className="pi pi-search search-icon-left"></i>

            <InputText
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search User"
              className="global-search-input text-primary"
            />

            {searchText && (
              <i
                className="pi pi-times search-icon-right"
                onClick={() => {
                  setSearchText("");
                  setCurrentPage(0);
                }}
              ></i>
            )}
          </div>
          <CSVLink
            data={csvData}
            headers={headers}
            filename={"Login-History-Report.csv"}
            className="btn btn-primary"
          >
            <i className="pi pi-upload me-2"></i>Export
          </CSVLink>
        </div>
      </div>
      {/* Data Table */}
      <DataTable
        value={loading ? skeletonData : results}
        emptyMessage={loading ? "" : "No data found"}
        className="custom-table"
        responsiveLayout="scroll"
      >
        {/* S.No */}
        <Column
          header="S. No."
          body={
            loading
              ? skeletonBody
              : (_rowData, { rowIndex }) =>
                  currentPage * rowsPerPage + rowIndex + 1
          }
          style={{ width: "80px", textAlign: "center" }}
          bodyClassName="text-center"
          headerClassName="text-center"
        />
        <Column
          field="displayName"
          header="Display Name"
          body={
            loading
              ? skeletonBody
              : (rowData) => renderValue(rowData.displayName)
          }
        />

        <Column
          field="userName"
          header="User Name"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.userName)
          }
        />

        <Column
          field="email"
          header="Email"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.email)
          }
        />

        <Column
          field="firstLoginDateTime"
          header="First Login Date & Time"
          body={
            loading
              ? skeletonBody
              : (rowData) =>
                  rowData.firstLoginDateTime
                    ? Moment(rowData.firstLoginDateTime + "Z").format(
                        "DD/MM/YYYY hh:mm A"
                      )
                    : "-"
          }
        />

        <Column
          field="privacyPolicyAcceptedDateTime"
          header="Privacy Policy Accepted Date & Time"
          body={
            loading
              ? skeletonBody
              : (rowData) =>
                  rowData.privacyPolicyAcceptedDateTime
                    ? Moment(rowData.privacyPolicyAcceptedDateTime + "Z").format(
                        "DD/MM/YYYY hh:mm A"
                      )
                    : "-"
          }
        />

        <Column
          field="status"
          header="Status"
          body={
            loading ? skeletonBody : (rowData) => renderValue(rowData.status)
          }
        />
      </DataTable>
      {/* Pagination */}
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
        
        <div className="d-flex align-items-center gap-3">

          <label className="fw-bold">Show Rows:</label>
          {loading ? (
            <Skeleton width="100px" height="38px" />
          ) : (
            <select
              className="form-select form-select-sm w-auto py-1 text-secondary custom-input-color"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
            >
              {[10, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          )}
                                  <span >
                Total Records: <span className="text-secondary">{totalRecords}</span>
              </span>
        </div>
        {loading ? (
          <div className="d-flex gap-2">
            {Array.from({ length: 5 }).map((_, idx) => (
              <Skeleton key={idx} width="40px" height="40px" />
            ))}
          </div>
        ) : (
          <div className="d-flex justify-content-center align-items-center gap-2">
            <button
              className="btn p-0 fs-5 pagination-item"
              style={{ width: "40px", height: "40px" }}
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            >
              &lt;
            </button>
            {generatePageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={index} className="mx-1 text-secondary">
                  ...
                </span>
              ) : (
                <button
                  key={index}
                  className={`btn p-0 pagination-item ${
                    currentPage + 1 === page ? "active-page" : ""
                  }`}
                  style={{ width: "40px", height: "40px" }}
                  onClick={() => setCurrentPage(page - 1)}
                >
                  {page}
                </button>
              )
            )}
            <button
              className="btn p-0 fs-5 pagination-item"
              style={{ width: "40px", height: "40px" }}
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
              }
              disabled={currentPage >= totalPages - 1}
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstLoginHistoryReport;